import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './MobileCart.css';
import CartReviewModal from '../../components/CartReviewModal';

// Currency symbols for formatting prices
const currencySymbols = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$', CHF: 'CHF', CNY: '¥', SEK: 'kr', NZD: 'NZ$',
  MXN: 'Mex$', SGD: 'S$', HKD: 'HK$', NOK: 'kr', KRW: '₩', TRY: '₺', RUB: '₽', INR: '₹', BRL: 'R$', ZAR: 'R', AED: 'د.إ'
};

const MobileCart = ({
  cartItems,
  setCartItems = () => {},
  removeFromCart,
  togglePaymentType,
  checkout,
  clearCart,
  userSettings,
  onClose,
  paymentSplitData,
  setPaymentSplitData,
  onLoadBuylist = () => {},
  onSaveForLater = () => {}
}) => {
  const { t } = useTranslation();
  const [subtotal, setSubtotal] = useState({ cash: 0, credit: 0 });
  const [expandedItem, setExpandedItem] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const fileInputRef = useRef(null);

  // Payment split local state
  const [isPaymentSplitExpanded, setIsPaymentSplitExpanded] = useState(false);
  const [cashAmountInput, setCashAmountInput] = useState('');
  const [creditAmountInput, setCreditAmountInput] = useState('');

  // Derived values from payment split data
  const isPriceSplitAllowed = userSettings?.priceSplit?.enabled !== false;
  const canUsePaymentSplit = isPriceSplitAllowed && typeof setPaymentSplitData === 'function';
  const cashPercentage = paymentSplitData?.cashPercentage || 0;
  const isPaymentSplitEnabled = canUsePaymentSplit && (paymentSplitData?.isEnabled || false);

  // Helper function to format prices with the correct currency symbol
  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    const currencySymbol = currencySymbols[userSettings?.currency] || '$';
    return `${currencySymbol}${price.toFixed(2)}`;
  };

  const currencySymbol = currencySymbols[userSettings?.currency] || '$';

  // Function to trigger file input for CSV import
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Function to import cart items from CSV
  const importFromCSV = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());

        if (lines.length < 2) {
          alert('CSV file is empty or has no data rows.');
          return;
        }

        // Parse header to find column indices
        const header = lines[0].split(',').map(h => h.trim().toLowerCase());
        const nameIndex = header.findIndex(h => h === 'name');
        const conditionIndex = header.findIndex(h => h === 'condition');
        const printingIndex = header.findIndex(h => h === 'printing');
        const languageIndex = header.findIndex(h => h === 'language');
        const quantityIndex = header.findIndex(h => h === 'quantity');
        const priceTypeIndex = header.findIndex(h => h === 'price type');
        const priceIndex = header.findIndex(h => h === 'price');

        if (nameIndex === -1) {
          alert('CSV must have a "Name" column.');
          return;
        }

        const importedItems = [];

        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));

          const name = values[nameIndex];
          if (!name || name === 'Total:') continue;

          const condition = conditionIndex !== -1 ? values[conditionIndex] : 'NM';
          const printing = printingIndex !== -1 ? values[printingIndex] : 'Normal';
          const language = languageIndex !== -1 ? values[languageIndex] : 'EN';
          const quantity = quantityIndex !== -1 ? parseInt(values[quantityIndex]) || 1 : 1;
          const isCash = priceTypeIndex !== -1 ? values[priceTypeIndex]?.toLowerCase() === 'cash' : true;
          const price = priceIndex !== -1 ? parseFloat(values[priceIndex]) || 0 : 0;

          importedItems.push({
            name,
            condition,
            printing,
            language,
            quantity,
            isCash,
            cashPrice: isCash ? price : 0,
            creditPrice: !isCash ? price : 0,
            cartItemId: `imported-${Date.now()}-${i}`,
            isImported: true
          });
        }

        if (importedItems.length > 0) {
          setCartItems(importedItems);
          alert(`Successfully imported ${importedItems.length} items from CSV.`);
        } else {
          alert('No valid items found in the CSV file.');
        }
      } catch (error) {
        console.error('Error importing CSV:', error);
        alert('Error importing CSV file. Please check the file format.');
      }

      event.target.value = '';
    };

    reader.readAsText(file);
  };

  // Function to export cart items to CSV
  // Note: quantity is always 1 now (separate line items)
  const exportToCSV = () => {
    if (cartItems.length === 0) return;

    const csvHeader = ['Name', 'Condition', 'Printing', 'Language', 'Quantity', 'Price Type', 'Price', 'Total'];

    const total = cartItems.reduce((sum, item) => {
      const price = item.isCash ? item.cashPrice : item.creditPrice;
      return sum + parseFloat(price);
    }, 0);

    const csvRows = cartItems.map(item => {
      const price = item.isCash ? item.cashPrice : item.creditPrice;

      // For video games, show variant and console instead of condition/printing
      const condition = item.isVideoGame ? (item.variant?.toUpperCase() || 'CIB') : item.condition;
      const printing = item.isVideoGame ? (item.consoleName || 'Video Game') : item.printing;

      return [
        item.name,
        condition,
        printing,
        item.language || 'EN',
        1, // quantity is always 1 now
        item.isCash ? 'Cash' : 'Credit',
        parseFloat(price).toFixed(2),
        parseFloat(price).toFixed(2) // Total is the same as price since quantity is 1
      ];
    });

    csvRows.push(['', '', '', '', '', '', 'Total:', total.toFixed(2)]);

    const csvContent = [
      csvHeader.join(','),
      ...csvRows.map(row => {
        const paddedRow = row.slice(0, 8);
        while (paddedRow.length < 8) paddedRow.push('');
        return paddedRow.map(cell => {
          const cellStr = String(cell).replace(/"/g, '""');
          return `"${cellStr}"`;
        }).join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    const date = new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `cart-export-${date}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    calculateSubtotal();
  }, [cartItems]);

  // Note: quantity is always 1 now (separate line items)
  const calculateSubtotal = () => {
    const totals = cartItems.reduce(
      (acc, item) => {
        const price = item.isCash ? item.cashPrice : item.creditPrice;
        return {
          cash: acc.cash + (item.isCash ? price : 0),
          credit: acc.credit + (!item.isCash ? price : 0),
        };
      },
      { cash: 0, credit: 0 }
    );

    setSubtotal(totals);
  };

  // Calculate total cash and credit prices for all items (regardless of current payment type selection)
  const totalCashPrice = cartItems.reduce((sum, item) => {
    return sum + parseFloat(item.cashPrice || 0);
  }, 0);
  const totalCreditPrice = cartItems.reduce((sum, item) => {
    return sum + parseFloat(item.creditPrice || 0);
  }, 0);

  // Calculate payment split amounts based on percentage (only if enabled)
  const splitCashAmount = isPaymentSplitEnabled
    ? (totalCashPrice * cashPercentage / 100)
    : 0;
  const splitCreditAmount = isPaymentSplitEnabled
    ? (totalCreditPrice * (100 - cashPercentage) / 100)
    : 0;
  const splitTotal = isPaymentSplitEnabled
    ? (splitCashAmount + splitCreditAmount)
    : 0;

  // handleQuantityChange removed - cart now uses separate line items

  const handleRemoveItem = (item) => {
    removeFromCart(item);
  };

  const handleTogglePaymentType = (item) => {
    togglePaymentType(item);
  };

  const handleCheckout = () => {
    // Call the checkout function to navigate to the checkout page
    if (typeof checkout === 'function') {
      checkout();
    }
  };

  const handleClearCart = () => {
    // Show confirmation dialog before clearing cart
    const confirmed = window.confirm(t('Are you sure you want to clear all items from your cart? This action cannot be undone.'));
    if (confirmed) {
      clearCart();
    }
  };

  const toggleItemExpand = (itemId) => {
    if (expandedItem === itemId) {
      setExpandedItem(null);
    } else {
      setExpandedItem(itemId);
    }
  };

  // Payment Split Functions
  const updatePaymentSplit = (newCashPercentage) => {
    if (!canUsePaymentSplit) return;

    const cashPercentageDecimal = newCashPercentage / 100;
    const creditPercentageDecimal = (100 - newCashPercentage) / 100;

    const cashAmount = totalCashPrice * cashPercentageDecimal;
    const creditAmount = totalCreditPrice * creditPercentageDecimal;

    setPaymentSplitData({
      cashPercentage: newCashPercentage,
      creditPercentage: 100 - newCashPercentage,
      cashAmount,
      creditAmount,
      isEnabled: true
    });
  };

  const togglePaymentSplitSection = () => {
    if (!canUsePaymentSplit) return;

    const newExpanded = !isPaymentSplitExpanded;
    setIsPaymentSplitExpanded(newExpanded);

    if (newExpanded && !isPaymentSplitEnabled) {
      updatePaymentSplit(50);
    } else if (!newExpanded) {
      setPaymentSplitData({
        cashPercentage: 0,
        creditPercentage: 100,
        cashAmount: 0,
        creditAmount: 0,
        isEnabled: false
      });
    }
  };

  const handleCashPercentageChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value >= 0 && value <= 100) {
      updatePaymentSplit(value);
      setCashAmountInput((totalCashPrice * value / 100).toFixed(2));
      setCreditAmountInput((totalCreditPrice * (100 - value) / 100).toFixed(2));
    }
  };

  const handleCashPercentageInputChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      updatePaymentSplit(0);
      setCashAmountInput('0.00');
      setCreditAmountInput(totalCreditPrice.toFixed(2));
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      updatePaymentSplit(numValue);
      setCashAmountInput((totalCashPrice * numValue / 100).toFixed(2));
      setCreditAmountInput((totalCreditPrice * (100 - numValue) / 100).toFixed(2));
    }
  };

  const handleCashPercentageInputBlur = (e) => {
    const value = parseFloat(e.target.value);
    if (isNaN(value) || value < 0) {
      updatePaymentSplit(0);
      setCashAmountInput('0.00');
      setCreditAmountInput(totalCreditPrice.toFixed(2));
    } else if (value > 100) {
      updatePaymentSplit(100);
      setCashAmountInput(totalCashPrice.toFixed(2));
      setCreditAmountInput('0.00');
    }
  };

  const handleCashAmountChange = (e) => {
    setCashAmountInput(e.target.value);
  };

  const handleCashAmountBlur = () => {
    const numValue = parseFloat(cashAmountInput);
    if (isNaN(numValue) || numValue < 0) {
      setCashAmountInput('0.00');
      updatePaymentSplit(0);
      setCreditAmountInput(totalCreditPrice.toFixed(2));
    } else if (numValue > totalCashPrice) {
      setCashAmountInput(totalCashPrice.toFixed(2));
      updatePaymentSplit(100);
      setCreditAmountInput('0.00');
    } else {
      const percentage = Math.min(100, Math.max(0, (numValue / totalCashPrice) * 100));
      updatePaymentSplit(percentage);
      setCashAmountInput(numValue.toFixed(2));
      setCreditAmountInput((totalCreditPrice * (100 - percentage) / 100).toFixed(2));
    }
  };

  const handleCreditAmountChange = (e) => {
    setCreditAmountInput(e.target.value);
  };

  const handleCreditAmountBlur = () => {
    const numValue = parseFloat(creditAmountInput);
    if (isNaN(numValue) || numValue < 0) {
      setCreditAmountInput('0.00');
      updatePaymentSplit(100);
      setCashAmountInput(totalCashPrice.toFixed(2));
    } else if (numValue > totalCreditPrice) {
      setCreditAmountInput(totalCreditPrice.toFixed(2));
      updatePaymentSplit(0);
      setCashAmountInput('0.00');
    } else {
      const creditPercentage = Math.min(100, Math.max(0, (numValue / totalCreditPrice) * 100));
      const cashPct = 100 - creditPercentage;
      updatePaymentSplit(cashPct);
      setCreditAmountInput(numValue.toFixed(2));
      setCashAmountInput((totalCashPrice * cashPct / 100).toFixed(2));
    }
  };

  // Sync amount inputs when cart changes or percentage changes
  useEffect(() => {
    if (!isPriceSplitAllowed && paymentSplitData?.isEnabled && typeof setPaymentSplitData === 'function') {
      setPaymentSplitData({
        cashPercentage: 0,
        creditPercentage: 100,
        cashAmount: 0,
        creditAmount: 0,
        isEnabled: false
      });
      setIsPaymentSplitExpanded(false);
      return;
    }

    if (isPaymentSplitEnabled) {
      setCashAmountInput((totalCashPrice * cashPercentage / 100).toFixed(2));
      setCreditAmountInput((totalCreditPrice * (100 - cashPercentage) / 100).toFixed(2));
    } else {
      setCashAmountInput('0.00');
      setCreditAmountInput('0.00');
    }
  }, [totalCashPrice, totalCreditPrice, cashPercentage, isPaymentSplitEnabled, isPriceSplitAllowed, paymentSplitData?.isEnabled, setPaymentSplitData]);

  if (cartItems.length === 0) {
    return (
      <div className="mobile-cart-empty">
        <p>{t('Your cart is empty')}</p>
        <button className="mobile-cart-back-button" onClick={onClose}>
          {t('Back to Search')}
        </button>
      </div>
    );
  }

  return (
    <div className="mobile-cart">
      <div className="mobile-cart-scroll-content">
      {/* Cart Header with action buttons */}
      <div className="mobile-cart-header">
        <h2 className="mobile-cart-title">{t('Your Cart')}</h2>
        <div className="mobile-cart-header-buttons">
          <button
            className="mobile-cart-header-button mobile-load-btn"
            onClick={onLoadBuylist}
            title={t('Load a previously saved buylist')}
          >
            📂
          </button>
          <button
            className="mobile-cart-header-button mobile-import-btn"
            onClick={triggerFileInput}
            title={t('Import items from CSV file')}
          >
            📥
          </button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept=".csv"
            onChange={importFromCSV}
          />
          <button
            className="mobile-cart-header-button mobile-export-btn"
            onClick={exportToCSV}
            disabled={cartItems.length === 0}
            title={t('Export cart to CSV file')}
          >
            📤
          </button>
          <button
            className="mobile-cart-header-button mobile-review-btn"
            onClick={() => setShowReviewModal(true)}
            disabled={cartItems.length === 0}
            title={t('Review consolidated cart')}
          >
            📋
          </button>
          <button
            className="mobile-cart-header-button mobile-clear-btn"
            onClick={handleClearCart}
            disabled={cartItems.length === 0}
            title={t('Clear all items')}
          >
            🗑️
          </button>
        </div>
      </div>

      <div className="mobile-cart-items">
        {cartItems.map((item) => {
          const isExpanded = expandedItem === item.cartItemId;
          const price = item.isCash ? item.cashPrice : item.creditPrice;
          // Note: quantity is always 1 now (separate line items)

          return (
            <div
              key={item.cartItemId}
              className={`mobile-cart-item ${isExpanded ? 'expanded' : ''}`}
            >
              <div
                className="mobile-cart-item-header"
                onClick={() => toggleItemExpand(item.cartItemId)}
              >
                <div className="mobile-cart-item-thumbnail">
                  <img
                    src={item.image || process.env.PUBLIC_URL + '/flags/No-Image-Placeholder.svg.png'}
                    alt={item.name}
                    className="mobile-cart-item-thumbnail-img"
                    onError={(e) => {
                      e.target.onerror = null; // Prevent infinite loop
                      e.target.src = process.env.PUBLIC_URL + '/flags/No-Image-Placeholder.svg.png';
                    }}
                  />
                </div>
                <div className="mobile-cart-item-info">
                  <div className="mobile-cart-item-name">{item.name}</div>
                  <div className="mobile-cart-item-details">
                    <span className="mobile-cart-item-condition">
                      {item.isVideoGame ? (item.variant?.toUpperCase() || 'CIB') : item.condition}
                    </span>
                    <span className="mobile-cart-item-price">{formatPrice(price)}</span>
                  </div>
                </div>
                <div className="mobile-cart-item-expand-icon">
                  {isExpanded ? '▲' : '▼'}
                </div>
              </div>

              {isExpanded && (
                <div className="mobile-cart-item-expanded">
                  <div className="mobile-cart-item-info">
                  <div className="mobile-cart-item-set">{item.isVideoGame ? item.consoleName : item.expansionName}</div>
                  <div className="mobile-cart-item-printing">
                    {item.isVideoGame ? `Variant: ${item.variant?.toUpperCase() || 'CIB'}` : `Print Type: ${item.printing || 'Normal'}`}
                  </div>
                  <div className="mobile-cart-item-type">
                    {item.isCash ? 'Cash' : 'Credit'} - {formatPrice(price)}
                  </div>
                  </div>

                  {/* Quantity controls removed - each item is a separate line */}
                  <div className="mobile-cart-item-controls">
                    <div className="mobile-cart-item-actions">
                      <button
                        className="mobile-cart-toggle-type"
                        onClick={() => handleTogglePaymentType(item)}
                      >
                        {item.isCash ? 'Switch to Credit' : 'Switch to Cash'}
                      </button>
                      <button
                        className="mobile-cart-remove"
                        onClick={() => handleRemoveItem(item)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      <div className="mobile-cart-summary">
        <div className="mobile-cart-totals">
          <div className="mobile-cart-subtotal">
            <span>Cash Subtotal:</span>
            <span>{formatPrice(subtotal.cash)}</span>
          </div>
          <div className="mobile-cart-subtotal">
            <span>Credit Subtotal:</span>
            <span>{formatPrice(subtotal.credit)}</span>
          </div>
          <div className="mobile-cart-total">
            <span>Total:</span>
            <span>{formatPrice(subtotal.cash + subtotal.credit)}</span>
          </div>
        </div>

        {/* Payment Split Section - Collapsible */}
        {canUsePaymentSplit && (
          <div className="mobile-payment-split-section">
            <div className="mobile-payment-split-header" onClick={togglePaymentSplitSection}>
              <h4 className="mobile-payment-split-title">
                Payment Split {isPaymentSplitEnabled && '(Active)'}
                <span
                  className="mobile-payment-split-info-icon"
                  title="Split your payment between cash and store credit. Slide towards cash to receive more in cash, or towards credit to receive more in store credit."
                  onClick={(e) => e.stopPropagation()}
                >
                  ℹ️
                </span>
              </h4>
              <span className={`mobile-collapse-icon ${isPaymentSplitExpanded ? 'expanded' : 'collapsed'}`}>
                {isPaymentSplitExpanded ? '▼' : '▶'}
              </span>
            </div>

            {isPaymentSplitExpanded && (
              <div className="mobile-payment-split-content">
                <div className="mobile-payment-split-slider">
                  <div className="mobile-slider-labels">
                    <span>Credit</span>
                    <span>Cash</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="0.1"
                    value={cashPercentage}
                    onChange={handleCashPercentageChange}
                    className="mobile-payment-slider"
                  />
                  <div className="mobile-percentage-input-container">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={cashPercentage}
                      onChange={handleCashPercentageInputChange}
                      onBlur={handleCashPercentageInputBlur}
                      className="mobile-percentage-input"
                    />
                    <span className="mobile-percentage-symbol">%</span>
                    <span className="mobile-percentage-label">Cash</span>
                  </div>
                </div>

                <div className="mobile-amount-inputs-container">
                  <div className="mobile-amount-input-group">
                    <label className="mobile-amount-input-label">Cash:</label>
                    <div className="mobile-amount-input-wrapper">
                      <span className="mobile-currency-prefix">{currencySymbol}</span>
                      <input
                        type="number"
                        min="0"
                        max={totalCashPrice}
                        step="0.01"
                        value={cashAmountInput}
                        onChange={handleCashAmountChange}
                        onBlur={handleCashAmountBlur}
                        className="mobile-amount-input"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="mobile-amount-input-group">
                    <label className="mobile-amount-input-label">Credit:</label>
                    <div className="mobile-amount-input-wrapper">
                      <span className="mobile-currency-prefix">{currencySymbol}</span>
                      <input
                        type="number"
                        min="0"
                        max={totalCreditPrice}
                        step="0.01"
                        value={creditAmountInput}
                        onChange={handleCreditAmountChange}
                        onBlur={handleCreditAmountBlur}
                        className="mobile-amount-input"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                <div className="mobile-payment-split-amounts">
                  <div className="mobile-split-amount">
                    <span>Cash: {currencySymbol}{splitCashAmount.toFixed(2)} ({cashPercentage.toFixed(1)}%)</span>
                  </div>
                  <div className="mobile-split-amount">
                    <span>Credit: {currencySymbol}{splitCreditAmount.toFixed(2)} ({(100 - cashPercentage).toFixed(1)}%)</span>
                  </div>
                  <div className="mobile-split-total">
                    <span>Split Total: {currencySymbol}{splitTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

      <div className="mobile-cart-actions">
        <button 
          className="mobile-cart-checkout"
          onClick={handleCheckout}
          disabled={cartItems.length === 0}
        >
          {t('Checkout')}
        </button>
        <button 
          className="mobile-cart-clear"
          onClick={handleClearCart}
          disabled={cartItems.length === 0}
        >
          {t('Clear Cart')}
        </button>
        <button
          className="mobile-cart-continue"
          onClick={onClose}
        >
          {t('Continue Shopping')}
        </button>
      </div>
      </div>
      </div>

      {/* Cart Review Modal */}
      <CartReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        cartItems={cartItems}
        userSettings={userSettings}
        onSave={onSaveForLater}
      />
    </div>
  );
};

export default MobileCart;
